/**
 * View Renderer Component
 * 
 * Renders a full view using either PageDoc (visual editor) or MDX content.
 * Provides a seamless viewing experience for published views.
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  CircularProgress,
  Alert,
  Toolbar,
  Tooltip,
  Breadcrumbs,
  Link,
  Chip,
  Container,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Refresh as RefreshIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  Public as PublicIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { View } from '../../types/view.types';
import { viewsApiService } from '../../services/views.api';
import { ViewPreview } from '../views/ViewPreview';
import { PageRenderer } from '../page-builder/PageRenderer';
import { ViewRenderProvider } from '../../context/ViewRenderContext';

const ViewerContainer = styled(Box)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: theme.palette.background.default,
}));

const ViewerHeader = styled(Paper)(({ theme }) => ({
  borderRadius: 0,
  borderBottom: `1px solid ${theme.palette.divider}`,
  zIndex: 1,
}));

const ViewerToolbar = styled(Toolbar)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(2),
}));

const ViewInfo = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minWidth: 0, // Allow text truncation
}));

const ViewerContent = styled(Box)(({ theme }) => ({
  flex: 1,
  overflow: 'auto',
  position: 'relative',
}));

const LoadingOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 'rgba(255, 255, 255, 0.9)',
  zIndex: 10,
}));

interface ViewRendererProps {
  view: View;
  organizationId: string;
  teamId?: string;
  onBack?: () => void;
  showHeader?: boolean;
}

export const ViewRenderer: React.FC<ViewRendererProps> = ({
  view,
  organizationId,
  teamId,
  onBack,
  showHeader = true,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [renderedContent, setRenderedContent] = useState<string | null>(null);

  useEffect(() => {
    // If view has MDX content, render it
    if (view.mdxContent && !view.pageDoc) {
      renderMDXContent();
    }
  }, [view]);

  const renderMDXContent = async () => {
    if (!view.mdxContent) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await viewsApiService.renderView(view.id, {
        organization_id: organizationId,
        team_id: teamId,
      });
      
      setRenderedContent(response.html);
    } catch (err) {
      console.error('Error rendering view:', err);
      setError('Failed to render view content. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    if (view.mdxContent && !view.pageDoc) {
      renderMDXContent();
    }
  };

  const handleFullscreen = () => {
    if (!isFullscreen) {
      document.documentElement.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
    setIsFullscreen(!isFullscreen);
  };

  return (
    <ViewRenderProvider viewId={view.id} pageDoc={view.pageDoc}>
    <ViewerContainer>
      {showHeader && (
        <ViewerHeader elevation={0}>
          <ViewerToolbar>
            {onBack && (
              <Tooltip title="Back to views">
                <IconButton onClick={onBack} edge="start">
                  <BackIcon />
                </IconButton>
              </Tooltip>
            )}
            
            <ViewInfo>
              <Breadcrumbs separator="›" sx={{ mb: 0.5 }}>
                <Link
                  underline="hover"
                  color="inherit"
                  onClick={onBack}
                  sx={{ cursor: 'pointer' }}
                >
                  Views
                </Link>
                <Typography color="text.primary">{view.name}</Typography>
              </Breadcrumbs>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {view.isPublic && (
                  <Chip
                    icon={<PublicIcon sx={{ fontSize: 14 }} />}
                    label="Public"
                    size="small"
                    variant="outlined"
                    sx={{ height: 20 }}
                  />
                )}
                <Typography variant="caption" color="text.secondary">
                  {view.description}
                </Typography>
              </Box>
            </ViewInfo>
            
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip title="Refresh">
                <IconButton onClick={handleRefresh} disabled={loading}>
                  <RefreshIcon />
                </IconButton>
              </Tooltip>

              <Tooltip title={isFullscreen ? "Exit fullscreen" : "Fullscreen"}>
                <IconButton onClick={handleFullscreen}>
                  {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
                </IconButton>
              </Tooltip>
            </Box>
          </ViewerToolbar>
        </ViewerHeader>
      )}
      
      <ViewerContent>
        {loading && (
          <LoadingOverlay>
            <CircularProgress />
          </LoadingOverlay>
        )}
        
        {error && (
          <Box sx={{ p: 3 }}>
            <Alert severity="error">{error}</Alert>
          </Box>
        )}
        
        {!loading && !error && (
          <>
            {/* Render PageDoc content if available */}
            {view.pageDoc && (
              <PageRenderer 
                pageDoc={view.pageDoc}
                organizationId={organizationId}
                teamId={teamId}
              />
            )}
            
            {/* Render MDX content if available and no PageDoc */}
            {view.mdxContent && !view.pageDoc && (
              <Container maxWidth="xl" sx={{ py: 3 }}>
                {renderedContent ? (
                  <Box dangerouslySetInnerHTML={{ __html: renderedContent }} />
                ) : (
                  <ViewPreview
                    viewId={view.id}
                    mdxContent={view.mdxContent}
                    viewData={view}
                    organizationId={organizationId}
                    teamId={teamId || ''}
                  />
                )}
              </Container>
            )}

            {/* Fallback if no content */}
            {!view.pageDoc && !view.mdxContent && (
              <Container maxWidth="xl">
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <Typography variant="h6" color="text.secondary">
                    This view has no content to display.
                  </Typography>
                </Box>
              </Container>
            )}
          </>
        )}
      </ViewerContent>
    </ViewerContainer>
    </ViewRenderProvider>
  );
};

export default ViewRenderer;